import {
  ArrowRight,
  CheckCircle,
  LockKey,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { readableApiError } from "../api";
import { defaultAuthenticatedPath } from "../experience";
import { useI18n } from "../i18n";
import { Brand } from "../components/Brand";
import { LanguageSwitch } from "../components/LanguageSwitch";

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  const auth = useAuth();
  const { locale, t } = useI18n();
  const location = useLocation();
  const returnToValue = new URLSearchParams(location.search).get("returnTo");
  const returnTo = returnToValue?.startsWith("/") && !returnToValue.startsWith("//") ? returnToValue : null;
  const [name, setName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (auth.status === "authenticated") {
    return <Navigate replace to={returnTo ?? defaultAuthenticatedPath(auth.workspace?.role, locale)} />;
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "register") {
        await auth.register({ name, workspaceName, email, password, locale });
      } else {
        await auth.login({ email, password });
      }
    } catch (caught) {
      setError(readableApiError(caught));
    } finally {
      setSubmitting(false);
    }
  };

  const alternatePath = mode === "login" ? "/register" : "/login";
  const alternateLabel =
    mode === "login" ? t("auth.createAccount") : t("auth.signIn");

  return (
    <main className={`auth-page auth-page--${mode}`}>
      <section aria-labelledby="auth-page-title" className="auth-panel">
        <header className="auth-panel__header">
          <Brand />
          <LanguageSwitch minimal />
        </header>
        <div className="auth-panel__body">
          <div className="auth-copy">
            <p className="auth-copy__eyebrow">
              {t("auth.eyebrow")}
            </p>
            <h1 id="auth-page-title">{mode === "login" ? t("auth.signIn") : t("auth.createAccount")}</h1>
            <p>
              {mode === "login"
                ? t("auth.loginDescription")
                : t("auth.registerDescription")}
            </p>
          </div>
          <form className="auth-form" onSubmit={(event) => void submit(event)}>
            {mode === "register" ? (
              <div className="auth-form__split">
                <label>
                  <span>{t("auth.name")}</span>
                  <input
                    autoComplete="name"
                    maxLength={80}
                    minLength={2}
                    onChange={(event) => setName(event.target.value)}
                    required
                    value={name}
                  />
                </label>
                <label>
                  <span>{t("auth.workspace")}</span>
                  <input
                    autoComplete="organization"
                    maxLength={100}
                    minLength={2}
                    onChange={(event) => setWorkspaceName(event.target.value)}
                    required
                    value={workspaceName}
                  />
                </label>
              </div>
            ) : null}
            <label>
              <span>{t("auth.email")}</span>
              <input
                autoComplete="email"
                inputMode="email"
                maxLength={254}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.com"
                required
                type="email"
                value={email}
              />
            </label>
            <label>
              <span>{t("auth.password")}</span>
              <input
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                maxLength={128}
                minLength={mode === "register" ? 12 : 1}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
              {mode === "register" ? (
                <small>{t("auth.passwordHint")}</small>
              ) : null}
            </label>
            {error ? (
              <p aria-live="assertive" className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <button className="button button--primary button--wide" disabled={submitting} type="submit">
              {submitting
                ? t("auth.wait")
                : mode === "login"
                  ? t("auth.signIn")
                  : t("auth.createAccount")}
              <ArrowRight aria-hidden="true" size={17} />
            </button>
          </form>
          <p className="auth-alternate">
            {mode === "login" ? t("auth.noAccount") : t("auth.hasAccount")} {" "}
            <Link to={`${alternatePath}?lang=${locale}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ""}`}>{alternateLabel}</Link>
          </p>
          <div className="auth-assurances">
            <span><LockKey aria-hidden="true" size={15} />{t("auth.sessionAssurance")}</span>
            <span><ShieldCheck aria-hidden="true" size={15} />{t("auth.isolationAssurance")}</span>
          </div>
        </div>
      </section>
      <section aria-label={t("auth.visualLabel")} className="auth-visual">
        <img
          alt={
            locale === "zh"
              ? "企业多模态知识汇入紫色可信知识中枢，再供给搜索与智能体"
              : "Enterprise multimodal knowledge flowing into a purple trusted knowledge core for search and Agents"
          }
          className="auth-visual__image"
          src="/assets/assay-knowledge-core-purple.png"
        />
        <div className="auth-visual__copy">
          <span><CheckCircle aria-hidden="true" size={17} weight="fill" />{t("auth.readOnly")}</span>
          <h2>{t("auth.welcome")}</h2>
          <p>{t("auth.description")}</p>
        </div>
        <div aria-hidden="true" className="auth-visual__signal">
          <span>{locale === "zh" ? "多模态知识" : "Multimodal knowledge"}</span>
          <strong>{locale === "zh" ? "可信知识中枢" : "Trusted knowledge core"}</strong>
          <span>{locale === "zh" ? "搜索 · 智能体 · 内容" : "Search · Agents · Content"}</span>
        </div>
        <div className="auth-visual__footer">
          <span>{t("auth.noMigration")}</span>
          <span>{t("auth.fullLineage")}</span>
          <span>{t("auth.permissionAware")}</span>
        </div>
      </section>
    </main>
  );
}
